import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InlineError, ShowError } from '../../../Components/Messages';

import { FormComponentProps } from '../../../common';
import { omit } from '../../../utils/object';
import { InvitationRecipientType } from '../../types';

type EnrollmentInvitationRecipientsFormProps = FormComponentProps<InvitationRecipientType[]> & {
  invitationRecipients: InvitationRecipientType[];
};

const EnrollmentInvitationRecipientsForm: React.FC<EnrollmentInvitationRecipientsFormProps> = memo(
  ({ title, invitationRecipients, onSave }) => {
    const { t } = useTranslation();

    const [selectedItems, setSelectedItems] = useState<InvitationRecipientType[]>(
      createSelectedItems(invitationRecipients)
    );
    const [itemEmails, setItemEmails] = useState<Record<string, string | undefined>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    const memoizedOnSave = useCallback(
      (saveData: any) => {
        onSave?.(saveData);
      },
      [onSave]
    );

    useEffect(() => {
      memoizedOnSave({ data: selectedItems });
    }, [memoizedOnSave, selectedItems]);

    const renderInvitationRecipientItems = useCallback(() => {
      return invitationRecipients.map((invitationRecipient) => {
        const { contactId } = invitationRecipient;
        const error = errors[`email-${contactId}`];
        const isSelected = !!selectedItems.find((item) => item.contactId === contactId);

        return (
          <div className="mb-4 flex items-center space-x-4" key={contactId}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                setErrors((prev) => omit(prev, ['noInvitationRecipients']));
                if (e.target.checked) {
                  if (!isSelected) setSelectedItems((prev) => [...prev, invitationRecipient]);
                } else {
                  if (isSelected) setSelectedItems((prev) => prev.filter((item) => item.contactId !== contactId));
                }
              }}
              className="form-checkbox h-5 w-5 text-blue-600"
              data-testid="invitation-recipient"
            />
            <div className="flex-grow" data-testid="ir-details">
              <h4 className="font-bold">{invitationRecipient.displayName}</h4>
              {invitationRecipient.email ? (
                <div>{invitationRecipient.email}</div>
              ) : (
                isSelected && (
                  <div className={`mt-2 ${error ? 'text-red-600' : ''}`}>
                    <input
                      type="email"
                      required
                      name={`email-${invitationRecipient.contactId}`}
                      placeholder={t('enrollments.invitationRecipientsEmailPlaceholder')}
                      value={itemEmails[invitationRecipient.contactId] ?? ''}
                      onChange={(e) => {
                        setErrors((prev) => omit(prev, [`email-${invitationRecipient.contactId}`]));
                        setItemEmails((prev) => ({ ...prev, [invitationRecipient.contactId]: e.target.value }));
                      }}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 ${
                        error ? 'border-red-500' : ''
                      }`}
                      data-testid="ir-email-address"
                    />
                    {!!error && <InlineError text={error} data-testid="ir-error-msg" />}
                  </div>
                )
              )}
            </div>
          </div>
        );
      });
    }, [errors, invitationRecipients, itemEmails, selectedItems, t]);

    const renderBanner = useCallback(() => {
      const bannerMessage = t('enrollments.invitationRecipientsSelectOne');
      const hasValidationErrors = Object.keys(errors).filter((key) => key !== 'noInvitationRecipients').length > 0;
      const hasNoRecipientsSelected = selectedItems.length === 0;

      if (hasValidationErrors) {
        return <ShowError errors={errors} />;
      } else if (hasNoRecipientsSelected) {
        return <ShowError icon="warning" content={bannerMessage} hideHeader={true} />;
      } else {
        return null;
      }
    }, [errors, selectedItems, t]);

    return (
      <div className="space-y-4" data-testid="ir-form">
        <h2 className="text-2xl font-bold" data-testid="ir-form-title">
          {t(title ?? 'enrollments.invitationRecipientsFormTitle')} <span className="text-red-900">*</span>
        </h2>
        {renderBanner()}
        <div className="space-y-4" data-testid="ir-items">
          {renderInvitationRecipientItems()}
        </div>
      </div>
    );
  }
);

EnrollmentInvitationRecipientsForm.displayName = 'EnrollmentInvitationRecipientsForm';

function createSelectedItems(invitationRecipients: InvitationRecipientType[]): InvitationRecipientType[] {
  return invitationRecipients.filter((invitationRecipient) => !!invitationRecipient.email);
}

export default EnrollmentInvitationRecipientsForm;
